import { atom, useAtom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import moment, { Moment } from 'moment';
import {
    ReactNode,
    SetStateAction,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
} from 'react';
import { z } from 'zod';

import { GraphInputHelpers } from '../../fetching/graphql/GraphInputHelpers';
import { DashboardVariation } from '../../types/organization';
import { atomWithLocalStorage } from '../../utils/browserStorage';
import { useQueryParam } from '../../utils/queryParams';
import { DashboardContainer, RootEntity } from './containers/types';
import {
    getDefaultEnabledContainerKeys,
    useDashboardVariationConfig,
} from './containers/variations/config';

interface IDashboardPageContext {
    variation: DashboardVariation;
    date: Date;
    dateMoment: Moment;
    dateLabel: string;
    dateInput: string | null;
    availableContainers: DashboardContainer<RootEntity>[];
    enabledContainers: DashboardContainer<RootEntity>[];
    toggleContainerEnabled: (containerKey: string) => void;
    selectedContainer: DashboardContainer<RootEntity> | null;
    setSelectedContainer: (container: DashboardContainer<RootEntity> | null) => void;
}

const DashboardPageContext = createContext<IDashboardPageContext | null>(null);

// Stores the user's enabled containers for each dashboard variation under a single
// key in local storage. We allow this configuration to persist across logout
// as it contains only UX preferences and no user-revealing data.
// We may end up storing these in the database in the future so that preferences
// are accessible across devices, but local storage gets the job done for now.
const enabledContainersByVariationAtom = atomWithLocalStorage(
    'enabledContainersByVariation',
    {},
    z.record(z.nativeEnum(DashboardVariation), z.array(z.string())),
);

// Derives an atom for a user's enabled containers for a single dashboard variation.
const enabledContainersForVariationAtom = atomFamily((variation: DashboardVariation) =>
    atom(
        (get) =>
            get(enabledContainersByVariationAtom)[variation] ??
            getDefaultEnabledContainerKeys(variation),
        (get, set, value: SetStateAction<string[]>) => {
            const prev = get(enabledContainersByVariationAtom);
            const nextValue =
                typeof value === 'function'
                    ? value(prev[variation] ?? getDefaultEnabledContainerKeys(variation))
                    : value;
            set(enabledContainersByVariationAtom, {
                ...prev,
                [variation]: nextValue,
            });
        },
    ),
);

// Global atom for the selected container. This persists in memory across navigation
// but does NOT persist to localStorage, so it resets on page refresh or new tabs.
const selectedContainerAtom = atom<DashboardContainer<RootEntity> | null>(null);

export const DashboardPageContextProvider = ({ children }: { children: ReactNode }) => {
    // Config from current dashboard variation
    const config = useDashboardVariationConfig();
    const availableContainers = config.containers;

    // Container state - use global atom to persist across navigation
    const [selectedContainer, setSelectedContainer] = useAtom(selectedContainerAtom);
    const [enabledContainerKeys, setEnabledContainerKeys] = useAtom(
        enabledContainersForVariationAtom(config.variation),
    );
    const enabledContainerKeySet = useMemo(
        () => new Set(enabledContainerKeys),
        [enabledContainerKeys],
    );
    const enabledContainers = availableContainers.filter((container) =>
        enabledContainerKeySet.has(container.key),
    );

    // Ensure that the selected container is enabled, otherwise, deselect it.
    useEffect(() => {
        if (
            selectedContainer &&
            !enabledContainers.some((container) => container.key === selectedContainer.key)
        ) {
            setSelectedContainer(null);
        }
    }, [enabledContainers, selectedContainer, setSelectedContainer]);

    // Container functions
    const toggleContainerEnabled = useCallback(
        (containerKey: string) => {
            setEnabledContainerKeys((prev) => {
                if (prev.includes(containerKey)) {
                    return prev.filter((key) => key !== containerKey);
                } else {
                    return [...prev, containerKey];
                }
            });
        },
        [setEnabledContainerKeys],
    );

    // If a date query parameter is provided, use that date. Otherwise, use
    // today's date.
    //
    // This date never changes automatically as time passes (e.g. when midnight
    // passes and "today" changes), only when the page is first loaded or when
    // the query parameter is explicitly changed.
    const [dateStr] = useQueryParam('date', '');
    const dateMoment = useMemo(() => {
        if (dateStr === '') {
            return moment().startOf('day');
        }
        // Strictly parse the date string, raising an error if the date is invalid.
        // It's okay for this to cause an error page to show, as this is an
        // internal debugging feature.
        return moment(dateStr, 'YYYY-MM-DD', true);
    }, [dateStr]);

    // Formats the current date for display, prefixing with 'Today, ' if the date
    // is today and suffixing with the year if it's not the current year.
    const isToday = dateMoment.isSame(moment(), 'day');
    const isThisYear = dateMoment.isSame(moment(), 'year');
    const dateLabel = `${isToday ? 'Today, ' : ''}${dateMoment.format('dddd, MMMM Do')}${
        isThisYear ? '' : ` ${dateMoment.format('YYYY')}`
    }`;
    const dateInput = GraphInputHelpers.date(dateMoment);

    return (
        <DashboardPageContext.Provider
            value={{
                variation: config.variation,

                // Selected date
                date: dateMoment.toDate(),
                dateMoment,
                dateLabel,
                dateInput,

                // Containers
                availableContainers,
                enabledContainers,
                selectedContainer,
                setSelectedContainer,
                toggleContainerEnabled,
            }}
        >
            {children}
        </DashboardPageContext.Provider>
    );
};

export const useDashboardPageContext = () => {
    const context = useContext(DashboardPageContext);
    if (!context) {
        throw new Error(
            'useDashboardPageContext must be used within a DashboardPageContextProvider',
        );
    }
    return context;
};
